import { useEffect, useMemo, useState } from "react";
import { api, type Device, type Named, type Paged, type Strategy, type User } from "../lib/api.js";
import { deviceId, platformOf, relativeTime, dateTime } from "../lib/format.js";
import { Drawer, Empty, Notice, Presence, useAsync } from "../components/ui.js";
import { savedHash, forgetHash } from "../lib/credentials.js";
import type { PageProps } from "./Shell.js";

/**
 * The machine inventory is the console's hero: an admin opens this to find a
 * machine and get into it. The row itself carries the primary action, so the
 * common case is one click with no drill-down.
 */
export default function Machines({ me, navigate, onToggleRail }: PageProps) {
  const [query, setQuery] = useState("");
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [selected, setSelected] = useState<Device | null>(null);

  const devices = useAsync(
    () => api.get<Paged<Device>>(`/api/console/devices?pageSize=500${onlineOnly ? "&online=1" : ""}`),
    [onlineOnly],
  );

  const rows = useMemo(() => {
    const all = devices.data?.data ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((d) =>
      [d.id, d.hostname, d.username, d.os, d.note, d.device_group_name, d.user_name]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [devices.data, query]);

  const onlineCount = (devices.data?.data ?? []).filter((d) => d.online).length;
  const canConnect = me.permissions.includes("*") || me.permissions.includes("device:connect");

  return (
    <>
      <header className="topbar">
        <button className="btn btn--ghost btn--sm" onClick={onToggleRail} aria-label="Menu">
          &#9776;
        </button>
        <div className="topbar__title">
          <h1>Machines</h1>
          <span className="topbar__hint">
            {onlineCount} of {devices.data?.data.length ?? 0} reachable
          </span>
        </div>
        <div className="topbar__actions">
          <label className="check" style={{ margin: 0 }}>
            <input
              type="checkbox"
              checked={onlineOnly}
              onChange={(e) => setOnlineOnly(e.target.checked)}
            />
            Reachable only
          </label>
          <div className="search">
            <span className="search__icon">&#9906;</span>
            <input
              type="search"
              placeholder="Filter by ID, name, user…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button className="btn btn--sm" onClick={devices.reload}>Refresh</button>
        </div>
      </header>

      <div className="content content--flush">
        {devices.error ? (
          <div style={{ padding: "1.25rem" }}><Notice>{devices.error}</Notice></div>
        ) : null}

        {!devices.loading && rows.length === 0 ? (
          <Empty
            title={query ? "No machines match that filter" : "No machines yet"}
            hint={
              query
                ? "Try a different ID, hostname or user."
                : "Machines appear here the first time they connect to this server. Build a preconfigured installer to get one enrolled."
            }
            action={
              !query ? (
                <button className="btn btn--primary" onClick={() => navigate("/generator")}>
                  Build an installer
                </button>
              ) : null
            }
          />
        ) : (
          <div className="tablewrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 132 }}>Status</th>
                  <th style={{ width: 128 }}>ID</th>
                  <th>Machine</th>
                  <th>Platform</th>
                  <th>Group</th>
                  <th>Assigned to</th>
                  <th style={{ width: 120 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr
                    key={d.guid}
                    data-selected={selected?.guid === d.guid}
                    onClick={() => setSelected(d)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <Presence online={d.online} since={relativeTime(d.last_online)} />
                    </td>
                    <td className="num">{deviceId(d.id)}</td>
                    <td>
                      <div className="truncate" style={{ maxWidth: 260 }}>
                        {d.hostname || <span className="faint">Unnamed</span>}
                      </div>
                      {d.username ? <div className="small faint">{d.username}</div> : null}
                    </td>
                    <td className="muted">{platformOf(d.os)}</td>
                    <td className="muted">{d.device_group_name || <span className="faint">&mdash;</span>}</td>
                    <td className="muted">{d.user_name || <span className="faint">&mdash;</span>}</td>
                    <td className="actions">
                      {canConnect ? (
                        <button
                          className="btn btn--primary btn--sm"
                          disabled={!d.online}
                          title={d.online ? "Open a remote session" : "This machine is not reachable"}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/session/${d.id}`);
                          }}
                        >
                          Connect
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected ? (
        <MachineDrawer
          device={selected}
          me={me}
          onClose={() => setSelected(null)}
          onChanged={() => {
            devices.reload();
            setSelected(null);
          }}
          onConnect={() => navigate(`/session/${selected.id}`)}
        />
      ) : null}
    </>
  );
}

function MachineDrawer({
  device,
  me,
  onClose,
  onChanged,
  onConnect,
}: {
  device: Device;
  me: { permissions: string[] };
  onClose: () => void;
  onChanged: () => void;
  onConnect: () => void;
}) {
  const [note, setNote] = useState(device.note);
  const [groupId, setGroupId] = useState(device.device_group_id ?? "");
  const [userId, setUserId] = useState(device.user_id ?? "");
  const [strategyId, setStrategyId] = useState(device.strategy_id ?? "");
  const [newId, setNewId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [hasSaved, setHasSaved] = useState<boolean | null>(null);
  const [newPassword, setNewPassword] = useState("");

  // Whether a password is stored for this machine, so it can be cleared.
  useEffect(() => {
    let alive = true;
    savedHash(device.id)
      .then((h) => { if (alive) setHasSaved(!!h); })
      .catch(() => { if (alive) setHasSaved(false); });
    return () => { alive = false; };
  }, [device.id]);

  const groups = useAsync(() => api.get<Named[]>("/api/console/device-groups"), []);
  const users = useAsync(() => api.get<Paged<User>>("/api/console/users?pageSize=500"), []);
  const strategies = useAsync(() => api.get<Strategy[]>("/api/console/strategies"), []);

  const canWrite = me.permissions.includes("*") || me.permissions.includes("device:write");
  const canDelete = me.permissions.includes("*") || me.permissions.includes("device:delete");

  async function save() {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await api.patch(`/api/console/devices/${device.guid}`, {
        note,
        device_group_id: groupId || null,
        user_id: userId || null,
        strategy_id: strategyId || null,
      });
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function changeId() {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await api.post<{ note?: string }>(
        `/api/console/devices/${device.guid}/change-id`,
        { id: newId },
      );
      setMessage(res.note ?? "ID updated.");
      setNewId("");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /** Asks a neighbour on the machine's network to send a magic packet. */
  async function wake() {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const r = await api.post<{ ok: boolean; message: string }>(
        `/api/console/devices/${encodeURIComponent(device.id)}/wake`);
      if (r.ok) setMessage(r.message);
      else setError(r.message);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await api.del(`/api/console/devices/${device.guid}`);
      onChanged();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <Drawer
      title={device.hostname || deviceId(device.id)}
      onClose={onClose}
      footer={
        <>
          {device.online ? (
            <button className="btn btn--primary" onClick={onConnect}>
              Connect
            </button>
          ) : (
            // A machine that is off cannot be connected to, so the useful
            // button there is the one that turns it on.
            <button className="btn btn--primary" onClick={wake} disabled={busy}>
              {busy ? "Waking…" : "Wake"}
            </button>
          )}
          {canWrite ? (
            <button className="btn" onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save changes"}
            </button>
          ) : null}
          <div className="spacer" />
          {canDelete ? (
            <button className="btn btn--danger btn--sm" onClick={remove} disabled={busy}>
              Remove
            </button>
          ) : null}
        </>
      }
    >
      <div className="stack">
        {error ? <Notice>{error}</Notice> : null}
        {message ? <Notice kind="ok">{message}</Notice> : null}

        <dl className="kv">
          <dt>Status</dt>
          <dd><Presence online={device.online} since={relativeTime(device.last_online)} /></dd>
          <dt>ID</dt>
          <dd className="mono">{deviceId(device.id)}</dd>
          <dt>Operating system</dt>
          <dd>{device.os || "—"}</dd>
          <dt>Signed-in user</dt>
          <dd>{device.username || "—"}</dd>
          <dt>Processor</dt>
          <dd>{device.cpu || "—"}</dd>
          <dt>Memory</dt>
          <dd>{device.memory || "—"}</dd>
          <dt>Client version</dt>
          <dd>{device.version || "—"}</dd>
          <dt>Last address</dt>
          <dd className="mono">{device.ip || "—"}</dd>
          <dt>First seen</dt>
          <dd>{dateTime(device.created_at)}</dd>
          <dt>Encryption key</dt>
          <dd>{device.has_pk ? "Registered" : <span className="faint">Not yet registered</span>}</dd>
        </dl>

        {canWrite ? (
          <>
            <div className="field">
              <label className="field__label">Group</label>
              <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                <option value="">No group</option>
                {(groups.data ?? []).map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field__label">Assigned to</label>
              <select value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">Nobody</option>
                {(users.data?.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>{u.display_name || u.name}</option>
                ))}
              </select>
              <span className="field__hint">
                The assigned person always keeps full access to this machine.
              </span>
            </div>

            <div className="field">
              <label className="field__label">Settings profile</label>
              <select value={strategyId} onChange={(e) => setStrategyId(e.target.value)}>
                <option value="">Inherit</option>
                {(strategies.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field__label">Note</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
            </div>

            <div className="panel">
              <div className="panel__body">
                <h3 style={{ marginBottom: "0.375rem" }}>Saved password</h3>
                <p className="small faint">
                  Stored as a one-way hash, so this machine connects without prompting.
                  The password itself is never kept.
                </p>
                {hasSaved === null ? (
                  <p className="small faint">Checking…</p>
                ) : hasSaved ? (
                  <div className="row">
                    <span className="tag tag--live">Saved</span>
                    <button
                      className="btn btn--danger btn--sm"
                      onClick={async () => {
                        try {
                          await forgetHash(device.id);
                          setHasSaved(false);
                          setMessage("Saved password cleared. You will be asked for it next time.");
                        } catch (e) {
                          setError((e as Error).message);
                        }
                      }}
                    >
                      Clear saved password
                    </button>
                  </div>
                ) : (
                  <div className="row">
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Save a password for this machine"
                    />
                    <button
                      className="btn"
                      disabled={!newPassword}
                      title="Stored only after the machine accepts it on the next connection"
                      onClick={async () => {
                        // The salt comes from the machine, so it can only be
                        // hashed during a session. Keep it for the next connect.
                        try {
                          sessionStorage.setItem(`rd.pw.${device.id}`, newPassword);
                          setNewPassword("");
                          setMessage(
                            "It will be used and saved the next time you connect to this machine.",
                          );
                        } catch {
                          setError("This browser refused to hold the password until you connect.");
                        }
                      }}
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="panel">
              <div className="panel__body">
                <h3 style={{ marginBottom: "0.375rem" }}>Change the ID</h3>
                <p className="small faint">
                  Updates the ID this server records and rewrites address book entries.
                  Apply the same ID on the machine itself to complete the change.
                </p>
                <div className="row">
                  <input
                    className="mono"
                    value={newId}
                    onChange={(e) => setNewId(e.target.value)}
                    placeholder="123456789"
                    inputMode="numeric"
                  />
                  <button className="btn" onClick={changeId} disabled={busy || !newId}>
                    Update
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </Drawer>
  );
}
